import {
  DefaultButton,
  DetailsListLayoutMode,
  DetailsRow,
  IColumn,
  Icon,
  IDetailsListProps,
  IDetailsRowStyles,
  Label,
  mergeStyles,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  SearchBox,
  SelectionMode,
  ShimmeredDetailsList,
  Stack,
} from '@fluentui/react';
import dateformat from 'dateformat';
import {
  collection,
  DocumentData,
  limit,
  onSnapshot,
  orderBy,
  query,
  QueryDocumentSnapshot,
  startAfter,
  Timestamp,
  where,
} from 'firebase/firestore';
import React, { Component } from 'react';

import { firestore } from '../../../App/firebase';
import { store } from '../../../App/store';
import shipmentStatuses from '../../../shared/shipment-statuses.json';
import { theme } from '../../../utils/theme';
import { commaNumber } from '../../NumberField';
import { getSummary } from '../New/Components/utils';
import FormActions from './FormActions';

enum FilterType {
  ALL = "Hepsi",
  NON_SHIPPED = "Gönderilmeyen",
  NON_PAID = "Ödeme Tamamlanmayan",
}
type Props = {
  focusIconKey?: string;
  actions?: (formId: string) => JSX.Element;
  compact?: boolean;
  filter?: (item: DocumentData) => boolean;
};
type State = {
  items: Item[];
  filterType: FilterType;
  filterQuery: string;
  isArchived: boolean;
  shimmer: boolean;
  isEnded: boolean;
};
type Item = {
  id: string;
  migrated?: boolean;
  invoiceId?: string;
  archived: boolean;
  displayName: string;
  created: Timestamp;
  lastUpdated: Timestamp;
  lastVersionId: string;
  creator: string;
  creatorName?: string;
  employeeNames?: string[];
  summary?: ReturnType<typeof getSummary>;
  shipment?: { status?: string };
};

export default class FormList extends Component<Props, State> {
  private _colRef = collection(firestore, "forms_v2");
  private _lastDoc?: QueryDocumentSnapshot<DocumentData>;
  private _lastLoadItemId?: string;
  private lastFilterType?: FilterType;
  private loadIndex = 0;
  private isAdmin: boolean;
  private pageSize: number;
  private unsubscribeSnapshots?: () => any;
  private columns: IColumn[];
  private lastFocusIconKey?: string;

  constructor(props: Props) {
    super(props);

    const state = store.getState();

    this.lastFocusIconKey = "UNSET";
    this.columns = [];
    this.isAdmin =
      state.auth.userdata?.admin === true ||
      state.auth.userdata?.rank === "warehouse";
    this.pageSize = this.isAdmin ? 1e3 : 99;
    this.state = {
      items: [],
      filterType: FilterType.ALL,
      filterQuery: "",
      isArchived: false,
      shimmer: true,
      isEnded: false,
    };

    this.loadItems();
    this.componentDidUpdate();
  }

  componentDidUpdate() {
    if (this.lastFilterType !== this.state.filterType) {
      this.lastFilterType = this.state.filterType;
      this.loadItems();
    }
    if (this.lastFocusIconKey === this.props.focusIconKey) {
      return;
    }

    this.lastFocusIconKey = this.props.focusIconKey;

    const isCompactCols = !!this.props.focusIconKey || this.props.compact;

    this.columns = [
      {
        key: "index",
        iconName: "NumberSymbol",
        isIconOnly: true,
        minWidth: 24,
        maxWidth: 24,
        name: "Index",
        data: { compact: true },
        onRender: (_, index) => (
          <Label>{typeof index === "number" ? index + 1 : ""}</Label>
        ),
      },
      {
        key: "saleId",
        minWidth: 16,
        flexGrow: 1,
        name: "Satış No",
        onRender: (item?: Item) => item?.id,
      },
      {
        key: "description",
        minWidth: 16,
        flexGrow: 4,
        name: "Açıklama",
        data: { compact: true },
        onRender: (item?: Item) => (
          <Stack
            horizontal
            tokens={{ childrenGap: 5 }}
            className={descStackStyles}
          >
            <Stack.Item>{item?.displayName}</Stack.Item>
            {item?.archived && (
              <Stack.Item>
                <Icon
                  iconName="Archive"
                  style={{ color: theme.palette.themePrimary }}
                />
              </Stack.Item>
            )}
            {item?.invoiceId && (
              <Stack.Item>
                <Icon
                  iconName="PageLink"
                  style={{ color: theme.palette.themePrimary }}
                />
              </Stack.Item>
            )}
            {item?.migrated ? (
              <Stack.Item>
                <MessageBar messageBarType={MessageBarType.info}>
                  Yeni Portal'dan
                </MessageBar>
              </Stack.Item>
            ) : undefined}
            {item?.summary?.unpaidTotal ? (
              <Stack.Item>
                <MessageBar messageBarType={MessageBarType.warning}>
                  K.B: {commaNumber(item.summary.unpaidTotal)} TL
                </MessageBar>
              </Stack.Item>
            ) : undefined}
            {item?.shipment?.status === "shipped" ? (
              <Stack.Item>
                <MessageBar messageBarType={MessageBarType.success}>
                  Teslim Oldu
                </MessageBar>
              </Stack.Item>
            ) : item?.shipment?.status === "urgent" ? (
              <Stack.Item>
                <MessageBar messageBarType={MessageBarType.severeWarning}>
                  Acil Bekliyor
                </MessageBar>
              </Stack.Item>
            ) : undefined}
          </Stack>
        ),
      },
      {
        key: "retail",
        minWidth: 16,
        flexGrow: 2,
        name: "Mağaza",
        onRender: (item?: Item) => item?.creatorName,
        data: { isAdminOnly: true },
      },
      {
        key: "creatorInfo",
        minWidth: 16,
        flexGrow: 2,
        name: "Oluşturan",
        onRender: (item?: Item) => item?.employeeNames?.join(", "),
      },
      {
        key: "lastEdited",
        minWidth: 16,
        flexGrow: 2,
        name: "Son Düzenleme",
        data: { compact: true },
        onRender: (item?: Item) =>
          dateformat(item?.lastUpdated.toDate(), "dd/mm/yyyy HH:MM"),
      },
      {
        key: "created",
        minWidth: 16,
        flexGrow: 2,
        name: "Oluşturma Tarihi",
        onRender: (item?: Item) =>
          dateformat(item?.created.toDate(), "dd/mm/yyyy HH:MM"),
      },
      {
        key: "actions",
        minWidth: 170,
        name: "Eylemler",
        data: { compact: true },
        onRender: (item?: Item) =>
          item ? (
            this.props.actions ? (
              this.props.actions(item.id)
            ) : (
              <FormActions
                formId={item.id}
                invoiceId={item.invoiceId}
                migrated={item.migrated}
                focusIconKey={this.props.focusIconKey}
              />
            )
          ) : (
            <>Hata</>
          ),
      },
    ];

    if (!this.isAdmin) {
      this.columns = this.columns.filter((v) => !v.data?.isAdminOnly);
    }

    if (isCompactCols) {
      this.columns = this.columns.filter((v) => v.data?.compact);
    }

    this.forceUpdate();
  }

  componentWillUnmount() {
    this.unsubscribeSnapshots?.();
  }

  render() {
    let items = this.state.items;

    if (this.state.filterQuery) {
      items = items.filter((item) =>
        [
          item.displayName,
          item.creatorName || "",
          ...(item.employeeNames || []),
          item.id.toString(),
        ].find((str) => str.toLowerCase().includes(this.state.filterQuery))
      );
    }

    return (
      <Stack tokens={{ childrenGap: 10 }}>
        <Stack horizontal tokens={{ childrenGap: 10 }}>
          {Object.keys(FilterType).map(_key => {
            const key = _key as keyof typeof FilterType;
            if (this.state.filterType === FilterType[key]) {
              return (
                <PrimaryButton
                  key={key}
                  text={FilterType[key]}
                />
              );
            } else {
              return (
                <DefaultButton
                  key={key}
                  text={FilterType[key]}
                  onClick={() => this.setState({ filterType: FilterType[key] })}
                />
              );
            }
          })}
          {this.isAdmin && !this.props.compact && (
            <SearchBox
              styles={{ root: { width: "35%" } }}
              placeholder={`Filtrele (Max ${commaNumber(this.pageSize)} form)`}
              onChange={(_, nv) => this.setState({ filterQuery: nv || "" })}
            />
          )}
        </Stack>
        {items.length === 0 && this.state.filterQuery ? (
          <MessageBar messageBarType={MessageBarType.warning}>
            Form bulunamadı
          </MessageBar>
        ) : (
          <ShimmeredDetailsList
            items={items}
            shimmerLines={this.pageSize}
            enableShimmer={this.state.shimmer}
            columns={this.columns}
            onRowDidMount={this.onRowDidMount.bind(this)}
            onRenderRow={this._onRenderRow!.bind(this)}
            selectionMode={SelectionMode.none}
            layoutMode={DetailsListLayoutMode.fixedColumns}
            className={detailsListStyles}
          />
        )}
        {/* <br />
        {!this.state.isEnded && <Spinner />}
        <br /> */}
      </Stack>
    );
  }

  onRowDidMount(item?: Item, index?: number) {
    const isLastItem = index === this.state.items.length - 1;
    if (!isLastItem || !item) return;

    if (this._lastLoadItemId === item.id) return;
    this._lastLoadItemId = item.id;

    // this.loadItems(); // ! not paginated, static for now
  }

  private _onRenderRow: IDetailsListProps["onRenderRow"] = (props) => {
    const customStyles: Partial<IDetailsRowStyles> = {};

    if (props) {
      if (props.itemIndex % 2 === 0) {
        customStyles.root = {
          backgroundColor: theme.palette.neutralLighterAlt,
        };
      }

      return <DetailsRow {...props} styles={customStyles} />;
    }

    return null;
  };

  async loadItems(stateOverride?: Partial<State>) {
    const { auth } = store.getState();
    const userId = auth.user?.uid;
    if (!userId) return;

    const index = ++this.loadIndex;
    this.loadIndex = index;

    this.setState({
      shimmer: true,
      items: [],
    });

    const state = stateOverride
      ? { ...this.state, ...stateOverride }
      : this.state;
    let queryConsts = [];

    if (state.filterType === FilterType.NON_SHIPPED) {
      const acceptStatusList = shipmentStatuses
        .filter((status) => status.key !== "shipped")
        .map((status) => status.key);
      queryConsts.push(where("shipment.status", "in", acceptStatusList));
      queryConsts.push(where("shipment.version", "==", "v2"));
    }
    if (state.filterType === FilterType.NON_PAID) {
      queryConsts.push(where("summary.hasRemainingFunds", "==", true));
    }
    console.log("applied filter", state.filterType);

    if (this.isAdmin !== true) {
      queryConsts.push(where("creator", "==", userId));
    }

    queryConsts.push(where("archived", "==", state.isArchived));
    queryConsts.push(orderBy("created", "desc"));

    if (this._lastDoc) {
      queryConsts.push(startAfter(this._lastDoc));
    }

    queryConsts.push(limit(this.pageSize));

    const q = query(this._colRef, ...queryConsts);
    let currentItems: Item[] = [];

    this.unsubscribeSnapshots?.();
    this.unsubscribeSnapshots = onSnapshot(q, (snap) => {
      const snapItems = snap.docs.map((d) => ({
        id: d.id,
        invoiceId: d.data()!.invoice?.id,
        ...d.data()!,
      })) as Item[];

      const changedIds = [
        ...snap.docs.map((doc) => doc.id),
        ...snap.docChanges().map((change) => change.doc.id),
      ];
      const filteredStateItems = currentItems.filter(
        (item) => !changedIds.includes(item.id)
      );
      let totalItems = [...filteredStateItems, ...snapItems];

      if (this.props.filter) {
        totalItems = totalItems.filter(this.props.filter);
      }

      if (this.loadIndex !== index) {
        return;
      }

      currentItems = totalItems;
      this.setState({
        ...state,
        items: totalItems,
        shimmer: false,
        isEnded: true,
      });
    });

    // const lastDoc = snap.docs[snap.docs.length - 1];
    // this._lastDoc = lastDoc;

    // this.setState({
    //   items: [...state.items, ...newItems],
    //   shimmer: false,
    //   isEnded: snap.docs.length === 0,
    // });
  }
}

const detailsListStyles = mergeStyles({
  ".ms-Viewport &": {
    width: "100%",
    overflow: "hidden",
  },
});
const descStackStyles = mergeStyles({
  "& *": {
    fontSize: "12px",
    minHeight: "none",
    height: "24px",
  },
  "& .ms-MessageBar-text": {
    margin: "4px",
  },
  "& .ms-MessageBar-icon": {
    margin: "7px 0 0 8px",
  },
});
